import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@stackje/shared-components/ui/button";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@stackje/shared-components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@stackje/shared-components/ui/select";
import { Label } from "@stackje/shared-components/ui/label";
import { SocialMediaPlatformSchema } from "@stackje/common/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { 
  faYoutube,
  faInstagram,
  faTiktok,
  faXTwitter,
  faPinterest,
  faWhatsapp,
  faFacebook,
  faLinkedin,
  faReddit,
  faDiscord,
  faTelegram,
  faSnapchat,
  faGithub,
  faGitlab,
  faTwitch,
  faVimeo,
  faMedium,
  faSpotify,
  faSoundcloud,
  faDribbble,
  faBehance,
  faMastodon,
  faWeixin,
  faLine,
  faVk,
} from "@fortawesome/free-brands-svg-icons";

// Zod schema for social media link validation
const socialMediaLinkSchema = z.object({
  platform: SocialMediaPlatformSchema,
  url: z.string().url("Please enter a valid URL (e.g., https://example.com)"),
  displayName: z.string().optional(),
});

type SocialMediaLinkFormData = z.infer<typeof socialMediaLinkSchema>;

interface SocialMediaEditFormDialogContentProps {
  isEditing: boolean;
  initialData?: SocialMediaLinkFormData;
  onSubmit: (data: SocialMediaLinkFormData) => void;
  editingLinkIndex?: number | null;
  linksLength?: number;
  existingPlatforms?: string[];
}

const getSocialIcon = (platform: string) => {
  switch (platform) {
    case 'email':
      return faEnvelope;
    case 'youtube':
      return faYoutube;
    case 'instagram':
      return faInstagram;
    case 'tiktok':
      return faTiktok;
    case 'x':
      return faXTwitter;
    case 'pinterest':
      return faPinterest;
    case 'whatsapp':
      return faWhatsapp;
    case 'facebook':
      return faFacebook;
    case 'linkedin':
      return faLinkedin;
    case 'reddit':
      return faReddit;
    case 'discord':
      return faDiscord;
    case 'telegram':
      return faTelegram;
    case 'snapchat':
      return faSnapchat;
    case 'github':
      return faGithub;
    case 'gitlab':
      return faGitlab;
    case 'twitch':
      return faTwitch;
    case 'vimeo':
      return faVimeo;
    case 'medium':
      return faMedium;
    case 'spotify':
      return faSpotify;
    case 'soundcloud':
      return faSoundcloud;
    case 'dribbble':
      return faDribbble;
    case 'behance':
      return faBehance;
    case 'mastodon':
      return faMastodon;
    case 'weixin':
      return faWeixin;
    case 'line':
      return faLine;
    case 'vk':
      return faVk;
    default:
      return faEnvelope;
  }
};

const getPlatformLabel = (platform: string) => {
  switch (platform) {
    case 'email':
      return 'Email';
    case 'youtube':
      return 'YouTube';
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'x':
      return 'X (Twitter)';
    case 'pinterest':
      return 'Pinterest';
    case 'whatsapp':
      return 'WhatsApp';
    case 'facebook':
      return 'Facebook';
    case 'linkedin':
      return 'LinkedIn';
    case 'reddit':
      return 'Reddit';
    case 'discord':
      return 'Discord';
    case 'telegram':
      return 'Telegram';
    case 'snapchat':
      return 'Snapchat';
    case 'github':
      return 'GitHub';
    case 'gitlab':
      return 'GitLab';
    case 'twitch':
      return 'Twitch';
    case 'vimeo':
      return 'Vimeo';
    case 'medium':
      return 'Medium';
    case 'spotify':
      return 'Spotify';
    case 'soundcloud':
      return 'SoundCloud';
    case 'dribbble':
      return 'Dribbble';
    case 'behance':
      return 'Behance';
    case 'mastodon':
      return 'Mastodon';
    case 'weixin':
      return 'WeChat';
    case 'line':
      return 'LINE';
    case 'vk':
      return 'VK';
    default:
      return platform;
  }
};

const getPlaceholderUrl = (platform: string) => {
  switch (platform) {
    case 'email':
      return 'mailto:hello@example.com';
    case 'youtube':
      return 'https://youtube.com/@username';
    case 'instagram':
      return 'https://instagram.com/username';
    case 'tiktok':
      return 'https://tiktok.com/@username';
    case 'x':
      return 'https://x.com/username';
    case 'pinterest':
      return 'https://pinterest.com/username';
    case 'whatsapp':
      return 'https://wa.me/1234567890';
    case 'facebook':
      return 'https://facebook.com/username';
    case 'linkedin':
      return 'https://linkedin.com/in/username';
    case 'reddit':
      return 'https://reddit.com/u/username';
    case 'discord':
      return 'https://discord.gg/invitecode';
    case 'telegram':
      return 'https://t.me/username';
    case 'snapchat':
      return 'https://snapchat.com/add/username';
    case 'github':
      return 'https://github.com/username';
    case 'gitlab':
      return 'https://gitlab.com/username';
    case 'twitch':
      return 'https://twitch.tv/username';
    case 'vimeo':
      return 'https://vimeo.com/username';
    case 'medium':
      return 'https://medium.com/@username';
    case 'spotify':
      return 'https://open.spotify.com/artist/artistId';
    case 'soundcloud':
      return 'https://soundcloud.com/username';
    case 'dribbble':
      return 'https://dribbble.com/username';
    case 'behance':
      return 'https://behance.net/username';
    case 'mastodon':
      return 'https://mastodon.social/@username';
    case 'weixin':
      return 'https://weixin.qq.com/';
    case 'line':
      return 'https://line.me/R/ti/p/@username';
    case 'vk':
      return 'https://vk.com/username';
    default:
      return 'https://example.com';
  }
};

const getPlaceholderDisplayName = (platform: string) => {
  switch (platform) {
    case 'email':
      return 'Contact us';
    case 'youtube':
    case 'instagram':
    case 'tiktok':
    case 'x':
    case 'pinterest':
      return '@username';
    case 'whatsapp':
      return 'Chat with us';
    case 'facebook':
    case 'linkedin':
    case 'reddit':
    case 'discord':
    case 'telegram':
    case 'snapchat':
    case 'github':
    case 'gitlab':
    case 'twitch':
    case 'vimeo':
    case 'medium':
    case 'spotify':
    case 'soundcloud':
    case 'dribbble':
    case 'behance':
    case 'mastodon':
    case 'line':
    case 'vk':
    case 'weixin':
      return 'WeChat';
    default:
      return 'Display name';
  }
};

export function SocialMediaEditFormDialogContent({
  isEditing,
  initialData,
  onSubmit,
  editingLinkIndex,
  linksLength,
  existingPlatforms = [],
}: SocialMediaEditFormDialogContentProps) {
  // Get the first available platform that's not already used
  const getDefaultPlatform = () => {
    if (initialData?.platform) return initialData.platform;
    const availablePlatforms = SocialMediaPlatformSchema.options.filter(
      p => !existingPlatforms.includes(p)
    );
    return availablePlatforms[0] || 'instagram';
  };

  const form = useForm<SocialMediaLinkFormData>({
    resolver: zodResolver(socialMediaLinkSchema),
    defaultValues: {
      platform: getDefaultPlatform(),
      url: initialData?.url || "",
      displayName: initialData?.displayName || "",
    },
    mode: "onChange",
  });

  const handleSubmit = form.handleSubmit((data) => {
    onSubmit(data);
  });

  const watchedPlatform = form.watch('platform');

  // Reset form when editing different link (initialData changes)
  useEffect(() => {
    if (initialData) {
      form.reset({
        platform: initialData.platform,
        url: initialData.url,
        displayName: initialData.displayName || "",
      });
    } else {
      // When adding new link, reset form with first available platform
      const availablePlatforms = SocialMediaPlatformSchema.options.filter(
        p => !existingPlatforms.includes(p)
      );
      form.reset({
        platform: availablePlatforms[0] || 'instagram',
        url: "",
        displayName: "",
      });
    }
  }, [initialData, form, existingPlatforms]);

  return (
    <DialogContent className="w-full h-[100dvh] sm:max-w-[425px] sm:w-[90vw] sm:h-auto sm:max-h-[85vh] overflow-hidden flex flex-col">
      <DialogHeader className="flex-shrink-0">
        <DialogTitle>{isEditing ? "Edit social media link" : "Add social media link"}</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-4 p-4 flex-1 overflow-y-auto min-w-0">
        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex flex-col mb-2">
            <p className="text-sm text-muted-foreground">
              Not all fields will be used for a given card type.
            </p>
            <p className="text-sm text-muted-foreground">
              <span className="text-red-500">*</span> indicates required fields
            </p>
          </div>
          <Label htmlFor="socialPlatform">
            Platform <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="platform"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger>
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={getSocialIcon(field.value)}
                          className="w-4 h-4"
                        />
                        {getPlatformLabel(field.value)}
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {SocialMediaPlatformSchema.options
                      .filter((platform) => {
                        // When editing, allow the current platform
                        if (isEditing && initialData?.platform === platform) {
                          return true;
                        }
                        // Otherwise, filter out platforms that already exist
                        return !existingPlatforms.includes(platform);
                      })
                      .map((platform) => (
                        <SelectItem key={platform} value={platform}>
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon
                              icon={getSocialIcon(platform)}
                              className="w-4 h-4"
                            />
                            {getPlatformLabel(platform)}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {fieldState.error && (
                  <p className="text-sm text-red-500">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="socialUrl">
            URL <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="url"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <ClearableInput
                  id="socialUrl"
                  name={`socialUrl${isEditing ? editingLinkIndex : linksLength}`}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={getPlaceholderUrl(watchedPlatform)}
                  autoCapitalize="none"
                />
                {fieldState.error && (
                  <p className="text-sm text-red-500">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="socialDisplayName">Display Name</Label>
          <Controller
            name="displayName"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="socialDisplayName"
                name={`socialDisplayName${isEditing ? editingLinkIndex : linksLength}`}
                value={field.value || ""}
                onChange={field.onChange}
                placeholder={getPlaceholderDisplayName(watchedPlatform)}
              />
            )}
          />
        </div>
      </div>
      <DialogFooter className="flex-shrink-0 sticky bottom-0 bg-background border-t pt-4 mt-auto">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={form.formState.isSubmitting || !form.formState.isValid}
          className="w-full"
        >
          {isEditing ? "Save changes" : "Add social media link"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
